import React, { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useErrorNotice } from '../../../hooks/useErrorNotice';
import { makeStyles } from '@material-ui/core/styles';
import { Button, Grow, Paper, Typography } from '@material-ui/core';
import * as Map from '../../../common/map';
import { parseKmlArea } from '../../../common/aoi';
import { Actions as MapActions } from '../../../store/ducks/map';
import { DrawerHelper } from '../../../store/ducks/map/header';
/**
 * @constant MAPS_READY_POLL
 * @description How often to look for the Google Maps script, in ms.
 * @memberof module:SHARED
 */
const MAPS_READY_POLL = 150;

/**
 * @constant MAPS_READY_TIMEOUT
 * @description How long to wait for the script before giving up and saying so, in ms.
 * @memberof module:SHARED
 */
const MAPS_READY_TIMEOUT = 20000;

export const DEFAULT_ZOOM = 9;

const useStyles = makeStyles((theme) => ({
  wrapper: {
    width: '100%',
    height: '100%',
    display: 'flex',
    justifyContent: 'center',
  },
  map: {
    width: '100%',
    height: '100%',
  },
  boxWrapper: {
    position: 'absolute',
    top: 16,
    zIndex: 10,
  },
  overlayBox: {
    padding: 16,
    margin: 0,
    display: 'flex',
    alignItems: 'center',
  },
  overlayText: {
    padding: '0px 16px 0px 8px',
  },
}));

const GoogleMap = ({
  style,
  onRegionDrawn = () => {},
  onLoad = () => {},
  onBoundsChanged = () => {},
  mapTypeControlOpt = {},
  mapOptions = {},
}) => {
  const currentlyDrawing = useSelector((state) => state.map.currentlyDrawing);
  const drawingMessage = useSelector((state) => state.map.drawingMessage);

  const dispatch = useDispatch();
  const [t] = useTranslation();
  /**
   * Problems are reported in the corner instead of in a blocking dialog - see {@link useErrorNotice}.
   */
  const notifyError = useErrorNotice();
  const classes = useStyles();

  const mapRef = useRef();
  const componentRef = useRef();

  let zoomLevel = window.sessionStorage.getItem('zoom_level');
  zoomLevel =
    zoomLevel !== 'undefined' && typeof zoomLevel !== 'undefined' && !isNaN(parseInt(zoomLevel))
      ? parseInt(zoomLevel)
      : DEFAULT_ZOOM;

  useEffect(() => {
    let cancelled = false;
    let timer = null;
    let waited = 0;

    /**
     * @function buildMap
     * @description Creates the map once the Google Maps script has arrived, retrying until it has.
     * Reloading straight onto a deep route runs this effect before the script is in place, and the
     * constructor threw against an undefined `window.google`, leaving the page blank.
     * @memberof module:SHARED
     * @returns {void}
     */
    const buildMap = () => {
      if (cancelled) return;
      if (!window.google?.maps?.Map) {
        if (waited >= MAPS_READY_TIMEOUT) {
          console.error('[GoogleMap] the Google Maps script did not load; the map cannot be drawn.');
          return;
        }
        waited += MAPS_READY_POLL;
        timer = setTimeout(buildMap, MAPS_READY_POLL);
        return;
      }
      mapRef.current = new window.google.maps.Map(componentRef.current, {
        center: { lat: -26.285757, lng: -48.73506 },
        zoom: zoomLevel,
        scaleControl: true,
        streetViewControl: false,
        disableDefaultUI: false,
        mapTypeControl: true,
        mapTypeControlOptions: mapTypeControlOpt,
        fullscreenControl: false,
        styles: [
          {
            featureType: 'poi.business',
            elementType: 'labels',
            stylers: [{ visibility: 'off' }],
          },
        ],
        ...mapOptions,
      });
      window.initMap = mapRef.current;
      Map.initializeMap(mapRef.current);
      Map.onRegionDrawn((overlay, coordinates, type) => {
        console.log('Region drawn triggered:', { overlay, coordinates, type });
        onRegionDrawn(overlay, coordinates, type);
        DrawerHelper.lastGenaratedOverlay = overlay;
        dispatch(MapActions.completeDrawing(overlay, coordinates));
      });
      window.google?.maps?.event?.addListener(mapRef.current, 'bounds_changed', () => {
        const bounds = mapRef.current.getBounds();
        if (onBoundsChanged && bounds) onBoundsChanged(bounds);
      });
      onLoad(mapRef.current);
    };

    buildMap();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, []);

  const showFilePanel = function () {
    let dialog = document.getElementById('dialog_kml');
    dialog.showModal();
  };

  const closeFilePanel = function () {
    let dialog = document.getElementById('dialog_kml');
    dialog.close();
  };

  const readFileData = function () {
    let fileField = document.getElementById('file_import_kml');
    if (fileField.files.length === 0) {
      notifyError(t('forms.core.fileErrors.selectFileFirst'));
      return false;
    }
    const file = fileField.files[0];
    const reader = new FileReader();
    reader.addEventListener(
      'load',
      function () {
        const fileContent = reader.result;
        let isKml = fileContent.indexOf('<coordinates>') !== -1;
        if (isKml) {
          const coordinatesArray = parseKmlArea(fileContent);
          if (coordinatesArray) {
            let overlay = Map.drawOutline(coordinatesArray);
            window.sessionStorage.setItem('coordinatesArray', coordinatesArray);
            Map.fitToCoordinates(coordinatesArray);
            closeFilePanel();
            dispatch(MapActions.completeDrawing(overlay, coordinatesArray));
          } else {
            notifyError(t('forms.core.fileErrors.kmlNoCoordinates'));
          }
        } else {
          notifyError(t('forms.core.fileErrors.unreadableFile'));
        }
      },
      false,
    );
    if (file) {
      reader.readAsText(file);
    }
  };
  return (
    <div className={classes.wrapper} style={{ ...style }}>
      <Grow in={currentlyDrawing === true}>
        <div className={classes.boxWrapper}>
          <Paper className={classes.overlayBox}>
            <Typography variant="body1" className={classes.overlayText}>
              {drawingMessage}
            </Typography>
            <Button color="secondary" variant="contained" onClick={() => dispatch(MapActions.cancelDrawing())}>
              {t('forms.map.cancel')}
            </Button>
            &nbsp;
            <Button color="primary" variant="contained" onClick={showFilePanel}>
              Import KML
            </Button>
            <dialog id="dialog_kml">
              <div style={{ display: 'block' }} id="file_kml">
                <fieldset style={{ borderColor: '#BBB' }} id="import_kml_component">
                  <legend>Arquivo KML</legend>
                  <input id="file_import_kml" accept=".kml" type="file" onChange={readFileData} />
                </fieldset>
              </div>
              <Button color="secondary" variant="contained" onClick={closeFilePanel}>
                {t('forms.map.cancel')}
              </Button>
            </dialog>
          </Paper>
        </div>
      </Grow>
      <div id="google-maps" ref={(r) => (componentRef.current = r)} className={classes.map}></div>
    </div>
  );
};

export default GoogleMap;
